/*
 * Highlight text on scroll — Osmo setup.
 * https://www.osmo.supply/resource/highlight-text-on-scroll
 *
 * Reveals a [data-highlight-text] element piece by piece, fading from a dim opacity up to full as it
 * scrolls through the viewport (scrubbed to scroll position). Requires gsap + ScrollTrigger (<head>)
 * + SplitText (loaded per-section just before this file, after smooth-scroll.js — so registered here).
 *
 * Markup: <wp-highlight-text> (display: contents) wraps the element. Split + tween are built in
 * connectedCallback inside gsap.matchMedia() (motion-allowed only — under reduced motion the text is
 * left untouched and fully visible) and reverted in disconnectedCallback.
 *
 * Split granularity: words by default, which reads well on paragraphs / rich text and keeps the DOM
 * light; data-highlight-split="chars" opts a short single-line heading into the finer char stagger.
 * Accessibility: SplitText's default aria ("auto") hides the split pieces and puts the text in an
 * aria-label on the element. That's only dependable on headings — aria-label isn't allowed on a <p>
 * or a plain <div> (rich text), and it would also hide any links inside. So only link-free headings
 * use "auto"; everything else uses "none", leaving the words themselves in the accessibility tree
 * (whole words, never chars, so they still read naturally) and links keeping their role and focus.
 *
 * Per-element overrides (data attributes):
 *   data-highlight-split         (default "words") — "words" | "chars"
 *   data-highlight-scroll-start  (default "top 90%")
 *   data-highlight-scroll-end    (default "center 40%")
 *   data-highlight-fade          (default 0.2)  — starting opacity
 *   data-highlight-stagger       (default 0.1 for chars, 0.3 for words)
 */
(function () {
  if (!window.gsap || !window.ScrollTrigger || !window.SplitText || customElements.get('wp-highlight-text')) return;
  gsap.registerPlugin(ScrollTrigger, SplitText);

  const readNumber = (el, name, fallback) => {
    const value = parseFloat(el.getAttribute(name));
    return isNaN(value) ? fallback : value;
  };

  class WpHighlightText extends HTMLElement {
    connectedCallback() {
      const el = this.querySelector('[data-highlight-text]');
      if (!el) return;

      const byChars = el.getAttribute('data-highlight-split') === 'chars';
      const start = el.getAttribute('data-highlight-scroll-start') || 'top 90%';
      const end = el.getAttribute('data-highlight-scroll-end') || 'center 40%';
      const fade = readNumber(el, 'data-highlight-fade', 0.2);
      // Words are ~5x fewer targets than chars, so a wider default stagger keeps a similar sweep.
      const stagger = readNumber(el, 'data-highlight-stagger', byChars ? 0.1 : 0.3);

      const aria = /^H[1-6]$/.test(el.tagName) && !el.querySelector('a') ? 'auto' : 'none';

      this.mm = gsap.matchMedia();
      this.mm.add('(prefers-reduced-motion: no-preference)', () => {
        // autoSplit re-splits on resize / font load; the tween returned from onSplit is reverted and
        // rebuilt with each split, so it always targets the current pieces.
        const split = SplitText.create(el, {
          type: byChars ? 'words, chars' : 'words',
          aria,
          autoSplit: true,
          onSplit: (self) =>
            gsap.fromTo(
              byChars ? self.chars : self.words,
              { autoAlpha: fade },
              {
                autoAlpha: 1,
                ease: 'none',
                stagger,
                scrollTrigger: { trigger: el, start, end, scrub: true },
              },
            ),
        });
        return () => split.revert();
      });
    }

    disconnectedCallback() {
      if (this.mm) this.mm.revert();
      this.mm = null;
    }
  }

  customElements.define('wp-highlight-text', WpHighlightText);
})();
